/** Booking approval (#401) as the member portal words it. */
import { estimateCharge, formatKr, type RoomPricing } from "@nodus/booking";
import type { ApprovalSettings, ApprovalState, BookingApproval, BookingApprovalPage, SettingSource } from "@/lib/api/booking-approvals";
import { clock, dayClock } from "@/lib/clock";

/** Where a booking-request notification links to. A PATH: the login redirect keeps only the path (plan R3). */
export const approvalPath = (id: number): string => `/my-bookings/approval/${id}`;

/** `/my-bookings/approval/42` → 42; anything else → null. */
export function approvalIdFromPath(pathname: string): number | null {
  const match = /^\/my-bookings\/approval\/(\d+)\/?$/.exec(pathname);
  return match ? Number(match[1]) : null;
}

/** The request lapses at the meeting's start rather than earlier (booked 1 h or less ahead, plan R9). */
export const lapsesAtStart = (expiresAt: string, start: string): boolean => new Date(expiresAt) >= new Date(start);

/** The badge on a booking in My bookings; null = none. ``timeZone``: the booking's room's site (IANA name,
 *  e.g. the row's ``site_time_zone``) — booking times show in the SITE's zone, not the viewer's browser;
 *  omitted, falls back to the browser's local time. */
export function approvalBadge(
  state: ApprovalState | string | undefined,
  expiresAt?: string | null,
  start?: string,
  timeZone?: string,
): { label: string; color: string } | null {
  if (state === "pending") {
    if (!expiresAt) return { label: "Awaiting approval", color: "orange" };
    if (start && lapsesAtStart(expiresAt, start)) {
      return { label: `Awaiting approval · lapses at ${clock(start, timeZone)} unless approved`, color: "orange" };
    }
    return { label: `Awaiting approval · held until ${dayClock(expiresAt, timeZone)}`, color: "orange" };
  }
  if (state === "approved") return { label: "Approved", color: "green" };
  return null;
}

/** The toast after a booking — the server's real outcome (plan R2). ``start`` = the booking's start;
 *  ``timeZone`` = the room's site (see ``approvalBadge``). */
export function bookingResultMessage(
  approval?: { state: string; expires_at: string | null } | null,
  start?: string,
  timeZone?: string,
): string {
  if (approval?.state === "pending") {
    if (approval.expires_at && start && lapsesAtStart(approval.expires_at, start)) {
      return `Sent for approval. If no company admin approves it before it starts at ${clock(start, timeZone)}, the request lapses and the room is released.`;
    }
    return approval.expires_at
      ? `Sent for approval — the room is held for you until ${dayClock(approval.expires_at, timeZone)}.`
      : "Sent for approval — the room is held for you meanwhile.";
  }
  return "Room booked successfully";
}

/** The warning after a find-free booking — only when it waits for approval (its "book now" start is the
 *  deadline itself, plan R9); null = booked, say nothing more. */
export function pendingWarning(booked: {
  start: string;
  site_time_zone?: string;
  approval?: { state: string; expires_at: string | null } | null;
}): string | null {
  return booked.approval?.state === "pending"
    ? bookingResultMessage(booked.approval, booked.start, booked.site_time_zone)
    : null;
}

/** What the booking dialog says before booking (plan R2); the server decides. null = nothing to say.
 *  Keys on ``settings.applies`` — never on ``requires_approval`` alone: a company without an admin, or the
 *  time before the switch (MR E), books straight through, and the dialog must not say otherwise. */
export function approvalNotice(args: {
  settings: ApprovalSettings | undefined;
  estimate: number | null;
  /** The booking's start (ISO): within ``short_notice_minutes`` the request lapses at the start (plan R9). */
  start: string;
  now?: Date;
}): string | null {
  const { settings, estimate, start, now = new Date() } = args;
  // ``can_edit`` is the server's own answer to "may decide" — a company admin or staff (plan R5), who book
  // straight through; the portal's ``is_tenant_admin`` leaves staff out.
  if (!settings || settings.can_edit || !settings.applies || estimate == null || estimate <= 0) return null;
  const cost = `This booking costs about ${formatKr(estimate)} kr`;
  if (Number(settings.monthly_allowance_sek) > 0) {
    return `${cost}. If it goes over your monthly allowance, it will be sent to your company admin for approval.`;
  }
  const minutesAhead = (new Date(start).getTime() - now.getTime()) / 60_000;
  if (minutesAhead < 1) {
    return `${cost} and needs your company admin's approval, but it starts now: unless an admin approves it at once, the request lapses and the room is released.`;
  }
  if (minutesAhead <= settings.short_notice_minutes) {
    return `${cost} and will be sent to your company admin for approval. It starts soon: if no admin approves it before the start, the request lapses and the room is released.`;
  }
  return `${cost} and will be sent to your company admin for approval. The room is held for you meanwhile.`;
}

/** `approvalNotice` for a room, as the `bookingNotice` prop of `@nodus/booking`'s dialogs. */
export function approvalNoticeFor(settings: ApprovalSettings | undefined, pricing: RoomPricing | null | undefined) {
  return (start: string, end: string): string | null =>
    approvalNotice({ settings, estimate: estimateCharge(pricing, start, end)?.amount ?? null, start });
}

export function settingsSourceText(source: SettingSource): string {
  if (source === "tenant") return "Set by your company.";
  if (source === "site") return "Your workspace's default.";
  return "The standard setting.";
}

/** The reason a decision was refused (the API's 403/409 detail), for a toast. */
export function decisionError(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : "Couldn't save the decision. Please try again.";
}

/** The cancel button on a My bookings card: the booker withdraws their own pending request; a colleague's
 *  pending request has none (its admins decide it in the approvals card); any other booking keeps "cancel". */
export function bookingCancelAction(booking: {
  approval_state?: string;
  booked_by_me: boolean;
}): "withdraw" | "cancel" | null {
  if (booking.approval_state !== "pending") return "cancel";
  return booking.booked_by_me ? "withdraw" : null;
}

/** The open-requests list as the page shows it (#434): the body is the first page (a plain array), the
 *  ``X-Total-Count`` header how many wait in all. A missing/unreadable header, or a body that isn't a list,
 *  degrades to what is there — never a crash. */
export function approvalPage(
  body: BookingApproval[],
  headers: Record<string, unknown> | undefined,
): BookingApprovalPage {
  const results = Array.isArray(body) ? body : [];
  // AxiosHeaders reads case-insensitively through ``get``; a plain object (tests, other clients) by its key.
  const get = (headers as { get?: (name: string) => unknown } | undefined)?.get;
  const raw = typeof get === "function" ? get.call(headers, "x-total-count") : headers?.["x-total-count"];
  const total = raw == null || raw === "" ? NaN : Number(raw);
  return { results, total: Number.isFinite(total) ? Math.max(total, results.length) : results.length };
}
